(() => {
  'use strict';

  const FEATURED_PARAM = 'featured';
  const MAX_PAGES_TO_SCAN = 20;
  const GRID_SELECTOR = [
    '#ResultsList ul.product-grid[data-testid="product-grid"]',
    '#ResultsList ul.product-grid[ref="grid"]',
    'ul.product-grid[data-testid="product-grid"]',
  ].join(', ');
  const ITEM_SELECTOR = 'li.product-grid__item';
  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]';

  const featuredHandle = getFeaturedHandle();

  if (!featuredHandle || !isCollectionTemplate()) return;

  let mutationObserver = null;
  let applyQueued = false;
  const fetchedItems = new Map();

  patchFetch();
  patchHistory();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  function init() {
    applyFeaturedProduct();
    observeCollectionRedraws();
  }

  function getFeaturedHandle() {
    const handle = new URLSearchParams(window.location.search)
      .get(FEATURED_PARAM)
      ?.trim()
      .toLowerCase();

    return handle && /^[a-z0-9][a-z0-9_-]*$/.test(handle) ? handle : '';
  }

  function isCollectionTemplate() {
    return (
      window.Theme?.template?.name === 'collection' ||
      window.ShopifyAnalytics?.meta?.page?.pageType === 'collection' ||
      window.location.pathname.includes('/collections/')
    );
  }

  function applyFeaturedProduct() {
    preserveFeaturedParam();

    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid) return;

    const visibleItem = findProductItem(grid, featuredHandle);

    if (visibleItem) {
      pinItem(grid, visibleItem);
      removeDuplicates(grid, visibleItem);
      return;
    }

    fetchFeaturedItem(grid).then((fetchedItem) => {
      if (!fetchedItem || !document.contains(grid)) return;

      const liveItem = findProductItem(grid, featuredHandle);
      if (liveItem) {
        pinItem(grid, liveItem);
        removeDuplicates(grid, liveItem);
        return;
      }

      const clonedItem = fetchedItem.cloneNode(true);
      pinItem(grid, clonedItem);
      removeDuplicates(grid, clonedItem);
    });
  }

  function preserveFeaturedParam() {
    document
      .querySelectorAll('form[action*="/collections/"], #ResultsList form, facets-form-component form')
      .forEach((form) => {
        if (form.querySelector(`input[name="${FEATURED_PARAM}"]`)) return;

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = FEATURED_PARAM;
        input.value = featuredHandle;
        form.appendChild(input);
      });

    document
      .querySelectorAll('a[href*="/collections/"], facet-remove-component[data-url]')
      .forEach((node) => {
        if (node.matches('a[href]')) {
          const href = withFeaturedParam(node.getAttribute('href'));
          if (href && href !== node.getAttribute('href')) {
            node.setAttribute('href', href);
          }
        }

        if (node.hasAttribute('data-url')) {
          const url = withFeaturedParam(node.getAttribute('data-url'));
          if (url && url !== node.getAttribute('data-url')) {
            node.setAttribute('data-url', url);
          }
        }
      });
  }

  function pinItem(grid, item) {
    item.dataset.featuredPinned = featuredHandle;
    item.classList.add('product-grid__item--featured-pinned');

    const firstProductItem = Array.from(grid.children).find((child) =>
      child.matches(ITEM_SELECTOR)
    );

    if (firstProductItem !== item) {
      grid.insertBefore(item, firstProductItem || grid.firstChild);
    }
  }

  function removeDuplicates(grid, keeper) {
    findAllProductItems(grid, featuredHandle).forEach((item) => {
      if (item !== keeper) item.remove();
    });
  }

  function findProductItem(root, handle) {
    return findAllProductItems(root, handle)[0] || null;
  }

  function findAllProductItems(root, handle) {
    return Array.from(root.querySelectorAll(ITEM_SELECTOR)).filter((item) =>
      Array.from(item.querySelectorAll(PRODUCT_LINK_SELECTOR)).some(
        (link) => getProductHandle(link.href) === handle
      )
    );
  }

  function getProductHandle(href) {
    try {
      const url = new URL(href, window.location.origin);
      const marker = '/products/';
      const index = url.pathname.indexOf(marker);

      if (index === -1) return '';

      return url.pathname.slice(index + marker.length).split('/')[0].toLowerCase();
    } catch {
      return '';
    }
  }

  async function fetchFeaturedItem(grid) {
    const cacheKey = `${window.location.pathname}::${featuredHandle}`;

    if (fetchedItems.has(cacheKey)) return fetchedItems.get(cacheKey);

    const urls = buildCollectionScanUrls(grid);

    for (const url of urls) {
      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) continue;

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const item = findProductItem(parsedDocument, featuredHandle);

        if (item) {
          fetchedItems.set(cacheKey, item);
          return item;
        }
      } catch {
        continue;
      }
    }

    fetchedItems.set(cacheKey, null);
    return null;
  }

  function buildCollectionScanUrls(grid) {
    const lastPage = getLastPage(grid);
    const urls = [];

    for (let page = 1; page <= lastPage; page += 1) {
      urls.push(buildCollectionUrl(page, true));
    }

    for (let page = 1; page <= lastPage; page += 1) {
      urls.push(buildCollectionUrl(page, false));
    }

    return Array.from(new Set(urls));
  }

  function getLastPage(grid) {
    const lastPageFromGrid = Number(grid.dataset.lastPage);

    if (Number.isFinite(lastPageFromGrid) && lastPageFromGrid > 0) {
      return Math.min(lastPageFromGrid, MAX_PAGES_TO_SCAN);
    }

    const pageNumbers = Array.from(document.querySelectorAll('a[href*="page="]'))
      .map((link) => {
        try {
          return Number(new URL(link.href, window.location.origin).searchParams.get('page'));
        } catch {
          return 1;
        }
      })
      .filter((page) => Number.isFinite(page));

    return Math.min(Math.max(1, ...pageNumbers), MAX_PAGES_TO_SCAN);
  }

  function buildCollectionUrl(page, keepCurrentFilters) {
    const url = new URL(window.location.href);

    if (!keepCurrentFilters) {
      url.search = '';
    }

    url.searchParams.set(FEATURED_PARAM, featuredHandle);

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    return url.toString();
  }

  function withFeaturedParam(value) {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);

      if (url.origin !== window.location.origin || !url.pathname.includes('/collections/')) {
        return value;
      }

      url.searchParams.set(FEATURED_PARAM, featuredHandle);

      return value.startsWith('http')
        ? url.toString()
        : `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return value;
    }
  }

  function observeCollectionRedraws() {
    if (mutationObserver) mutationObserver.disconnect();

    mutationObserver = new MutationObserver(() => {
      if (applyQueued) return;

      applyQueued = true;
      window.requestAnimationFrame(() => {
        applyQueued = false;
        applyFeaturedProduct();
      });
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function patchFetch() {
    if (window.__collectionFeaturedProductFetchPatched) return;
    window.__collectionFeaturedProductFetchPatched = true;

    const originalFetch = window.fetch;

    window.fetch = function patchedFetch(input, init) {
      try {
        if (input instanceof Request) {
          const url = withFeaturedParam(input.url);

          if (url && url !== input.url) {
            input = new Request(url, input);
          }
        } else if (typeof input === 'string' || input instanceof URL) {
          input = withFeaturedParam(String(input)) || input;
        }
      } catch {
        return originalFetch.apply(this, arguments);
      }

      return originalFetch.call(this, input, init);
    };
  }

  function patchHistory() {
    if (window.__collectionFeaturedProductHistoryPatched) return;
    window.__collectionFeaturedProductHistoryPatched = true;

    ['pushState', 'replaceState'].forEach((method) => {
      const originalMethod = history[method];

      history[method] = function patchedHistoryState(state, title, url) {
        if (url) {
          return originalMethod.call(this, state, title, withFeaturedParam(String(url)) || url);
        }

        return originalMethod.apply(this, arguments);
      };
    });
  }
})();
